import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { SUPPORTED_LANGUAGES, type Language } from '../lib/i18n';
import { getLocaleLanguage, isPublicLocale, normalizePublicLocale, type PublicLocale } from '../lib/locales';

const STORAGE_KEY = 'tablegreet_signage_lang';
const supportedCodes = SUPPORTED_LANGUAGES.map(language => language.code);
const listeners = new Set<() => void>();

const isLanguage = (value: string | null): value is Language =>
  Boolean(value && supportedCodes.includes(value as Language));

const getStoredLanguage = (): Language => {
  if (typeof window === 'undefined') return 'en';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (isLanguage(saved)) return saved;
  const browserLang = navigator.language.split('-')[0];
  return isLanguage(browserLang) ? browserLang : 'en';
};

const getStoredLocale = (): PublicLocale | null => {
  if (typeof window === 'undefined') return null;
  const savedLocale = window.localStorage.getItem(`${STORAGE_KEY}_locale`);
  return isPublicLocale(savedLocale) ? normalizePublicLocale(savedLocale) : null;
};

const applyDocumentLanguage = (language: Language, locale?: PublicLocale | null) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = locale || language;
};

const emitLanguageChange = () => {
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useLanguage = () => {
  const currentLanguage = useSyncExternalStore(subscribe, getStoredLanguage, () => 'en' as Language);
  const currentLocale = useSyncExternalStore(subscribe, getStoredLocale, () => null);

  useEffect(() => {
    applyDocumentLanguage(currentLanguage, currentLocale);
  }, [currentLanguage, currentLocale]);

  const setLanguage = useCallback((newLanguage: Language) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, newLanguage);
    emitLanguageChange();
  }, []);

  const setPublicLocale = useCallback((locale: PublicLocale) => {
    if (typeof window === 'undefined') return;
    const normalized = normalizePublicLocale(locale);
    window.localStorage.setItem(`${STORAGE_KEY}_locale`, normalized);
    const language = getLocaleLanguage(normalized);
    window.localStorage.setItem(STORAGE_KEY, language);
    emitLanguageChange();
  }, []);

  return {
    currentLanguage,
    currentLocale,
    setLanguage,
    setPublicLocale,
    supportedLanguages: SUPPORTED_LANGUAGES,
    isRTL: currentLanguage === 'ar',
  };
};
